import React, { useState } from 'react';
import { Container, Row, Col, Card, Alert, Image } from 'react-bootstrap';
import './App.css';

function App() {
  const [lightboxImage, setLightboxImage] = useState(null);

  const openLightbox = (src, alt) => {
    setLightboxImage({ src, alt });
  };

  const closeLightbox = () => {
    setLightboxImage(null);
  };

  return (
    <div className="App">
      {/* Top Disclaimer */}
      <Alert variant="warning" className="m-0 text-center disclaimer-alert">
        <strong>DISCLAIMER:</strong> This website is purely for educational purposes and is an artistic expression
        of First Amendment rights as covered by Rogers v. Grimaldi. The author is not affiliated or associated
        with Mallory Ridge Apartment Homes or Red Tail Residential.
      </Alert>

      <Container className="my-5">
        <Row>
          <Col lg={8} className="mx-auto">
            {/* Business Info and Rating */}
            <Card className="shadow-lg mb-4">
              <Card.Body className="text-center p-4">
                <h2 className="mb-2 text-dark">Mallory Ridge Apartment Homes</h2>
                <p className="text-muted small mb-1">Managed by Red Tail Residential</p>
                <p className="mb-3 text-muted">
                  <strong>95 Filley St, Bloomfield, CT 06002</strong>
                </p>
                <div className="star-rating">
                  <span className="star filled">★</span>
                  <span className="star">★</span>
                  <span className="star">★</span>
                  <span className="star">★</span>
                  <span className="star">★</span>
                  <span className="rating-text ms-2">1 out of 5 stars</span>
                </div>
              </Card.Body>
            </Card>

            <Card className="shadow-lg">
              <Card.Header className="bg-primary text-white text-center">
                <h1 className="mb-0">Resident Review</h1>
                <p className="mb-0 mt-2">Educational Content - First Amendment Expression</p>
              </Card.Header>

              <Card.Body className="p-4">
                {/* Review Text */}
                <div className="mb-4">
                  <h3 className="text-danger mb-3">My Experience</h3>
                  <p className="lead text-muted mb-3">
                    <em>(this was somehow removed from Google reviews, so I'm reposting it here for posterity)</em>
                  </p>

                  <div className="review-content">
                    <p>
                      I have had multiple serious issues living at this property, and I would not recommend it.
                      I experienced a home invasion, and on a separate occasion all four wheels were stolen from
                      my vehicle while parked on the property. The parking lots are poorly lit at night, and
                      overall security is clearly inadequate.
                    </p>

                    <p>
                      The property itself is poorly maintained. There is dog waste and garbage throughout the
                      grounds, and management does not address it unless a complaint is filed with the Health Department.
                    </p>

                    <p>
                      Rent is extremely high and increases every year, yet basic amenities are unreliable.
                      The treadmill and gym television are frequently out of service. During winter, snow and
                      ice are not cleared or salted in a timely manner, creating unsafe conditions.
                    </p>

                    <p>
                      Parking enforcement is inconsistent and unfair. The contracted tow company does not respond
                      when vehicles block residents or park illegally, yet my car was towed within hours of a
                      registration expiration (<a href="https://www.cga.ct.gov/2025/ba/pdf/2025HB-07162-R010958-BA.pdf" target="_blank" rel="noopener noreferrer" className="text-primary">violating CT law on nonconsensual towing</a>).
                    </p>

                    <Alert variant="light" className="my-3 border-start border-4 border-info">
                      <small className="text-muted">
                        <strong>CT Law Reference:</strong> "The bill generally requires 14 days' notice before parking facility
                        (including residential parking facility) owners and lessees, or their
                        agents, may authorize towing a vehicle from the parking facility solely
                        because the vehicle has an expired registration."
                      </small>
                    </Alert>

                    <p>
                      Additionally, residents are required to pay rent electronically, which includes a $10 service
                      fee, a practice that is illegal. (<a href="https://www.cga.ct.gov/2013/ACT/PA/2013PA-00035-R00SB-00114-PA.htm" target="_blank" rel="noopener noreferrer" className="text-primary">Public Act No. 13-35</a>)
                    </p>

                    <Alert variant="light" className="my-3 border-start border-4 border-warning">
                      <small className="text-muted">
                        <strong>CT Public Act 13-35:</strong> "For any lease or rental agreement executed on or after October 1, 2013, no landlord of residential real property shall require electronic funds transfer as the exclusive form of payment of rent or a security deposit."
                      </small>
                    </Alert>

                    <p>
                      Management is rarely available in the office, slow to respond to concerns, and often dismissive
                      or rude when residents seek help. Overall, management appears more focused on maximizing profit
                      than maintaining a safe, clean, and livable environment for tenants.
                    </p>

                    <p className="text-danger fw-bold">
                      Nothing about this property justifies the price, the risk, or the treatment of tenants.
                    </p>
                  </div>
                </div>

                {/* Evidence Section */}
                <div className="evidence-section">
                  <h3 className="text-info mb-3">Documented Evidence</h3>

                  <Row className="g-4">
                    <Col md={6}>
                      <Card className="h-100">
                        <Card.Header className="bg-light">
                          <h5 className="mb-0">Property Maintenance Issues</h5>
                        </Card.Header>
                        <Card.Body className="text-center">
                          <Image
                            src="garbage.webp"
                            alt="Garbage and debris around property"
                            fluid
                            className="mb-2 clickable-image"
                            style={{maxHeight: '250px', objectFit: 'cover', cursor: 'pointer'}}
                            onClick={() => openLightbox('garbage.webp', 'Garbage and debris around property')}
                          />
                          <p className="small text-muted">
                            Garbage strewn about and broken fence around garbage bins
                          </p>
                        </Card.Body>
                      </Card>
                    </Col>

                    <Col md={6}>
                      <Card className="h-100">
                        <Card.Header className="bg-light">
                          <h5 className="mb-0">Security Issues</h5>
                        </Card.Header>
                        <Card.Body className="text-center">
                          <Image
                            src="wheels.webp"
                            alt="Vehicle with stolen wheels"
                            fluid
                            className="mb-2 clickable-image"
                            style={{maxHeight: '250px', objectFit: 'cover', cursor: 'pointer'}}
                            onClick={() => openLightbox('wheels.webp', 'Vehicle with stolen wheels')}
                          />
                          <p className="small text-muted">
                            All 4 wheels stolen due to insufficient lighting
                          </p>
                        </Card.Body>
                      </Card>
                    </Col>

                    <Col xs={12}>
                      <Card>
                        <Card.Header className="bg-light">
                          <h5 className="mb-0">Safety Incidents</h5>
                        </Card.Header>
                        <Card.Body className="text-center">
                          <video
                            controls
                            className="w-100"
                            style={{maxHeight: '400px'}}
                            preload="metadata"
                          >
                            <source src="arrest.mp4" type="video/mp4" />
                            Your browser does not support the video tag.
                          </video>
                          <p className="small text-muted mt-2">
                            Violent criminal arrest in parking lot after home invasion and domestic dispute
                          </p>
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>
                </div>

                {/* Legal Notice */}
                <Alert variant="info" className="mt-4">
                  <Alert.Heading>Legal Notice</Alert.Heading>
                  <p className="mb-0">
                    This content represents a factual account of personal experiences and is protected
                    under the First Amendment as artistic expression per Rogers v. Grimaldi precedent.
                  </p>
                </Alert>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Bottom Disclaimer */}
      <Alert variant="warning" className="m-0 text-center disclaimer-alert">
        <strong>DISCLAIMER:</strong> This website is purely for educational purposes and is an artistic expression
        of First Amendment rights as covered by Rogers v. Grimaldi. The author is not affiliated or associated
        with Mallory Ridge Apartment Homes or Red Tail Residential.
      </Alert>

      {/* Lightbox Modal */}
      {lightboxImage && (
        <div className="lightbox-overlay" onClick={closeLightbox}>
          <div className="lightbox-content">
            <button className="lightbox-close" onClick={closeLightbox}>×</button>
            <img
              src={lightboxImage.src}
              alt={lightboxImage.alt}
              className="lightbox-image"
            />
            <p className="lightbox-caption">{lightboxImage.alt}</p>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;